import React, { Component } from "react";
import { Card, Container, Form } from "react-bootstrap";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import CardLessDataTableWithSearch from "../../datatables/CardLessDataTableWithSearch";
import SelectDropDown from "../../selectdropdown/SelectDropDown";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import TaskUpdateModal from "./taskUpdateModal";
import { formatDateLegacy as formatDate } from "../../../../components/formatDateAndTime/formatDateAndTime";
import LeadWaChatConvo from "./leadWaChatConvo";

const GET_LEADS_LIST = gql`
  query getLeadsList($instituteId: Int!, $branchId: Int, $statusId: Int, $assignedEmpId: Int, $search: String, $page: Int, $limit: Int) {
    getLeadsList(institute_id: $instituteId, branch_id: $branchId, status_id: $statusId, assigned_emp_id: $assignedEmpId, search: $search, page: $page, limit: $limit) {
      totalCount
      leads {
        id
        student_name
        student_mobile
        employee_name
        status_name
        status_id
        latest_comment
        branch_name
        next_followup_date
        source_name
        inserted_at
      }
    }
  }
`;

const GET_LEAD_DASHBOARD_SUMMARY = gql`
  query getLeadDashboardSummary($instituteId: Int!, $branchId: Int, $assignedEmpId: Int) {
    getLeadDashboardSummary(institute_id: $instituteId, branch_id: $branchId, assigned_emp_id: $assignedEmpId) {
      totalLeads
      statusMetrics {
        status_id
        label
        count
      }
      followUpMetrics {
        today
        completed
        pending
      }
    }
  }
`;

const THEME = {
    primary: "#3b82f6",
    success: "#10b981",
    danger: "#ef4444",
    warning: "#f59e0b",
    text: "#0f172a",
    textMuted: "#64748b",
    border: "#e2e8f0",
    bgCard: "#ffffff",
    bgLight: "#f8fafc",
};

class MyTask extends Component {
    constructor(props) {
        super(props);
        this.scrollRef = React.createRef();
        this.state = {
            getMyTaskDetails: [],
            statusCounts: [],
            activeIndex: 0,
            selectedBranchDept: null,
            selectedTags: null,
            selectedRowData: {},
            openUpdateModal: false,
            openContactEditModal: false,
            openLeadChatConvo: false,
            snackbar: { open: false, message: "", severity: "success" },
            loading: false,
            error: null,
        };
    }

    componentDidMount() {
        this.fetchMyTasks();
    }

    fetchMyTasks = async () => {
        const { client } = this.props;
        const instituteId = Cookies.get("institutionid");
        const employeeId = Cookies.get("id") || Cookies.get("employee_id");
        this.setState({ loading: true });
        
        try {
            // Fetch both leads list and dashboard summary in parallel
            const [summaryRes, listRes] = await Promise.all([
                client.query({
                    query: GET_LEAD_DASHBOARD_SUMMARY,
                    variables: { 
                        instituteId: parseInt(instituteId) || 0,
                        assignedEmpId: parseInt(employeeId) || 0 
                    },
                    fetchPolicy: "network-only"
                }),
                client.query({
                    query: GET_LEADS_LIST,
                    variables: { 
                        instituteId: parseInt(instituteId) || 0,
                        assignedEmpId: parseInt(employeeId) || 0,
                        page: 1,
                        limit: 100 // Fetching more for local filtering/display
                    },
                    fetchPolicy: "network-only"
                })
            ]);
            
            const summary = summaryRes.data.getLeadDashboardSummary;
            const leads = listRes.data.getLeadsList.leads || [];
            
            // Transform summary metrics to the state structure
            const statusCounts = [
                { status_name: "Total Leads", status_count: summary.totalLeads },
                ...summary.statusMetrics.map(m => ({ status_name: m.label, status_count: m.count })),
                { status_name: "Today Followups", status_count: summary.followUpMetrics.today },
                { status_name: "Followups Completed", status_count: summary.followUpMetrics.completed },
                { status_name: "Followups Pending", status_count: summary.followUpMetrics.pending }
            ];

            const transformedData = [
                {
                    status_name: "My Tasks",
                    status_count: leads.length,
                    lead_details: leads.map((lead, index) => ({
                        ...lead,
                        name: lead.student_name, // Mapping student_name to name
                        followup_time: lead.next_followup_date,
                        comments: lead.latest_comment,
                        branch_names: lead.branch_name,
                        sno: index + 1
                    }))
                }
            ];
            
            this.setState({
                getMyTaskDetails: transformedData,
                statusCounts: statusCounts,
                loading: false
            });
        } catch (error) {
            console.error("Error fetching tasks:", error);
            this.setState({ 
                loading: false, 
                snackbar: { 
                    open: true, 
                    message: "Failed to fetch tasks", 
                    severity: "error" 
                } 
            });
        }
    };

    buildOptions = (arr, idKey, nameKey) => {
        const map = new Map();
        let hasUnassigned = false;

        arr.forEach(item => {
            let ids = item[idKey];
            let names = item[nameKey];

            if (!ids) {
                hasUnassigned = true;
                return;
            }

            const idList = ids.split(",").map(x => x.trim());
            const nameList = (names || "").split(",").map(x => x.trim());

            idList.forEach((id, idx) => {
                if (id && !map.has(id)) {
                    map.set(id, {
                        value: id,
                        label: nameList[idx] || id
                    });
                }
            });
        });

        const options = Array.from(map.values());
        if (hasUnassigned) {
            options.unshift({
                value: "0",
                label: "Un Assign"
            });
        }
        return options;
    };

    getTableColumns = () => [
        { dataField: "sno", text: "SNO" },
        {
            dataField: "name", text: "Name",
            formatter: (cell, row) => (
                <span
                    onClick={() => this.setState({ openContactEditModal: true, selectedRowData: row })}
                    style={{ cursor: "pointer", color: THEME.primary, fontWeight: 500 }}
                >
                    {cell}
                </span>
            ),
        },
        { dataField: "employee_name", text: "Employee Name" },
        {
            dataField: "lead_status_type_name", text: "Status",
            formatter: (cell) => (
                <span style={{
                    display: "inline-block",
                    backgroundColor: getStatusColor(cell),
                    color: "white",
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: 600,
                }}>
                    {cell}
                </span>
            ),
        },
        { dataField: "comments", text: "Comments" },
        { dataField: "branch_names", text: "Branch" },
        { dataField: "department_names", text: "Department" },
        { dataField: "tags", text: "Tag" },
        {
            dataField: "followup_time", text: "Next FollowUp Date",
            // followup_time is stored as a Unix epoch-seconds string; convert to
            // milliseconds for formatDate. Fall back to raw value for any legacy
            // date strings.
            formatter: (cell) => {
                if (!cell || cell === "-") return "-";
                const isUnix = /^\d+$/.test(String(cell));
                return formatDate(isUnix ? Number(cell) * 1000 : cell);
            },
        },
        {
            dataField: "id", text: "Actions",
            formatter: (cell, row) => (
                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                    <button
                        style={{ background: "transparent", border: "none", color: THEME.success, cursor: "pointer", fontSize: "16px", padding: "4px 8px" }}
                        onClick={() => this.setState({ openLeadChatConvo: true })}
                        title="WhatsApp"
                    >
                        <i className="fab fa-whatsapp" />
                    </button>
                    <button
                        style={{ background: "transparent", border: "none", color: THEME.primary, cursor: "pointer", fontSize: "16px", padding: "4px 8px" }}
                        onClick={() => this.setState({ openUpdateModal: true, selectedRowData: row })}
                        title="Edit"
                    >
                        <i className="fas fa-edit" />
                    </button>
                </div>
            ),
        },
    ];

    getFilteredLeads = () => {
        const { getMyTaskDetails, activeIndex, selectedBranchDept, selectedTags } = this.state;
        const leads = getMyTaskDetails[activeIndex]?.lead_details ?? [];

        if (!leads.length) return [];

        const branchId = selectedBranchDept?.value;
        const tagId = selectedTags?.value;

        if (!branchId && !tagId) {
            return leads;
        }

        return leads.filter(lead => {
            const leadBranches = lead.branch_department_id
                ? lead.branch_department_id.split(",").map(x => x.trim())
                : ["0"];

            const leadTags = lead.tag_id
                ? lead.tag_id.split(",").map(x => x.trim())
                : ["0"];

            const branchMatch = !branchId || leadBranches.includes(String(branchId));
            const tagMatch = !tagId || leadTags.includes(String(tagId));

            return branchMatch && tagMatch;
        });
    };

    getBranchDeptOptions = () => {
        const { getMyTaskDetails, activeIndex } = this.state;
        const leads = getMyTaskDetails[activeIndex]?.lead_details ?? [];
        return this.buildOptions(leads, "branch_department_id", "department_names");
    };

    getTagOptions = () => {
        const { getMyTaskDetails, activeIndex } = this.state;
        const leads = getMyTaskDetails[activeIndex]?.lead_details ?? [];
        return this.buildOptions(leads, "tag_id", "tags");
    };

    handleBranchChange = (name, value, selectedOption) => {
        this.setState({ selectedBranchDept: selectedOption });
    };

    handleTagChange = (name, value, selectedOption) => {
        this.setState({ selectedTags: selectedOption });
    };



    render() {
        const { getMyTaskDetails, activeIndex, openUpdateModal, selectedRowData, openContactEditModal, snackbar, loading, openLeadChatConvo } = this.state;
        const filteredLeads = this.getFilteredLeads();

        const { statusCounts } = this.state;
        
        const COLOR_MAP = {
            "total leads": "#ff9500",
            "open": "#3b82f6",
            "inprocess": "#8b5cf6",
            "admitted": "#10b981",
            "rejected": "#ef4444",
            "today followups": "#f59e0b",
            "followups completed": "#06b6d4",
            "followups pending": "#64748b",
            "hot": "#ef4444",
            "warm": "#f59e0b",
            "cold": "#3b82f6"
        };

        const statusData = statusCounts.map(status => ({
            key: status.status_name.toLowerCase().replace(/\s+/g, "_"),
            label: status.status_name,
            count: status.status_count,
            color: COLOR_MAP[status.status_name.toLowerCase()] || "#64748b",
            isBold: status.status_name === "Total Leads"
        }));

        return (
            <Container fluid>
                {/* Status Pills */}
                <div style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "24px",
                    marginTop: "16px",
                    flexWrap: "wrap",
                    overflowX: "auto",
                    paddingBottom: "8px",
                }}>
                    {statusData.map((status) => (
                        <div
                            key={status.key}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 16px",
                                borderRadius: "20px",
                                border: `2px solid ${status.color}`,
                                backgroundColor: status.isBold ? status.color : "white",
                                color: status.isBold ? "white" : status.color,
                                fontWeight: status.isBold ? 600 : 500,
                                fontSize: "13px",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = `0 4px 12px ${status.color}40`;
                                e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.transform = "translateY(0)";
                            }}
                        >
                            <span>{status.label}</span>
                            <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "28px",
                                height: "28px",
                                backgroundColor: status.isBold ? "rgba(255,255,255,0.3)" : `${status.color}20`,
                                borderRadius: "50%",
                                fontWeight: 700,
                            }}>
                                {status.count}
                            </span>
                        </div>
                    ))}
                </div>



                {/* Data Table */}
                {loading ? (
                    <div style={{ padding: "32px", textAlign: "center", color: THEME.textMuted }}>
                        Loading...
                    </div>
                ) : (
                    <CardLessDataTableWithSearch
                        parentData={filteredLeads}
                        particlecolumns={this.getTableColumns()}
                        defaultSorted={[{ dataField: "sno", order: "asc" }]}
                        name="leads"
                    />
                )}

                {/* Task Update Modal */}
                <TaskUpdateModal
                    show={openUpdateModal}
                    handleClose={() => this.setState({ openUpdateModal: false })}
                    rowData={selectedRowData}
                    type="myTask"
                />

                {/* Snackbar */}
                <SnackbarMessage
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => this.setState({ snackbar: { open: false, message: "", severity: "success" } })}
                />

                {/* Lead Chat Conversation */}
                <LeadWaChatConvo
                    open={openLeadChatConvo}
                    handleClose={() => this.setState({ openLeadChatConvo: false })}
                />
            </Container>
        );
    }
}

const getStatusColor = (status) => {
    const statusColors = {
        "Open": "#3b82f6",
        "Inprocess": "#8b5cf6",
        "Admitted": "#10b981",
        "Rejected": "#ef4444",
        "Contacted": "#f59e0b",
        "Follow-up": "#06b6d4",
        "Qualified": "#10b981",
    };
    return statusColors[status] || "#6b7280";
};

export default withApollo(MyTask);
